import { DeleteFilled } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Col, Row, Space } from "antd";
import { ColumnsType } from "antd/lib/table";
import Paragraph from "antd/lib/typography/Paragraph";
import SortableTable from "components/SortableTable";
import { useAuthenticatedRequest, useAuthenticatedRequestCreator } from "hooks/useRequests";
import { Rule } from "models/rules";
import NewRuleModal from "./Rules/NewRuleModal";
import TestRulesModal from "./Rules/TestRulesModal";
import SettingsHeader from "./SettingsHeader";

const useCardTitleRules = () => {
    const queryClient = useQueryClient();
    const ruleQueryKey = ['website-settings', 'card-title-rules'];
    const mutationOptions = {
        onSuccess: () => queryClient.invalidateQueries(ruleQueryKey)
    };

    const fetchRules = useAuthenticatedRequest<Rule[]>({
        method: 'get',
        url: '/website-settings/card-title-rules'
    });

    const addRule = useAuthenticatedRequestCreator<void, Rule>(
        (rule) => ({
            method: 'post',
            url: '/website-settings/card-title-rules/add',
            data: rule
        })
    );

    const reorderRules = useAuthenticatedRequestCreator<void, Rule[]>(
        (rules) => ({
            method: 'post',
            url: '/website-settings/card-title-rules/re-order',
            data: rules.map((r, idx) => ({ id: r.id, ordinal: idx + 1 }))
        })
    );

    const removeRule = useAuthenticatedRequestCreator<void, string>(
        (ruleId) => ({
            method: 'post',
            url: `/website-settings/card-title-rules/${ruleId}/remove`
        })
    );

    const { data, isLoading } = useQuery(ruleQueryKey, fetchRules);

    const { mutateAsync: saveRule, isLoading: isCreating } = useMutation(addRule, mutationOptions);

    const { mutateAsync: changeRuleOrder, isLoading: isSaving } = useMutation(reorderRules, mutationOptions);

    const { mutateAsync: deleteRule, isLoading: isDeleting } = useMutation(removeRule, mutationOptions);

    return {
        rules: data,
        isWorking: isLoading || isCreating || isSaving || isDeleting,
        saveRule,
        changeRuleOrder,
        deleteRule
    };
}

const CardTitlePreQASection = () => {
    const {
        rules = [],
        isWorking,
        saveRule,
        changeRuleOrder,
        deleteRule
    } = useCardTitleRules();

    const columns: ColumnsType<Rule> = [
        {
            key: 'actions',
            render: (_, rule) => <Button danger
                icon={<DeleteFilled />}
                onClick={async () => await deleteRule(rule.id)}
            />,
            width: 50,
        },
        {
            title: 'Order',
            dataIndex: 'ordinal',
            width: 100,
            align: 'center',
            render: v => `#${v}`
        },
        {
            title: 'Rule',
            dataIndex: 'description'
        }
    ]

    return <Row gutter={[32, 16]} style={{ marginBottom: '32px' }}>
        <Col span={24}>
            <SettingsHeader title="Card Title Processing" />
            <Paragraph>
                Update the current batch number, and/or define pre-QA processing rules for card titles. These rules will be executed <br />
                in-order as a pipeline (output of one becomes input of next) when card titles are imported from a 3rd party vendor.
            </Paragraph>
        </Col>
        <Col span={16}>
            <Space direction="vertical">
                <Space>
                    <NewRuleModal
                        onRuleCreated={saveRule}
                    />
                    <TestRulesModal />
                </Space>
                <SortableTable
                    columns={columns}
                    data={rules}
                    isLoading={isWorking}
                    onReorder={async (reorderedRules) => await changeRuleOrder(reorderedRules)}
                    scroll={{ y: 300 }}
                />
            </Space>
        </Col>
    </Row>
};

export default CardTitlePreQASection;